import React from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import type { AppointmentStatusDistributionResponse } from '../../../types/dashboard.types';
import type { TooltipProps } from 'recharts';
import styles from './Charts.module.css';
import { useTokenColors } from '../../../lib/useTokenColors';

// AB#2193 — status colours follow the semantic status tokens (as the status badges do);
// Recharts writes them into SVG attributes, so they are resolved via useTokenColors.
const STATUS_TOKEN_NAMES = ['primary', 'warning-fg', 'destructive-fg', 'success-fg'] as const;

const tooltipContentStyle = {
  backgroundColor: 'var(--bg-elevated)',
  color: 'var(--text-primary)',
  border: '1px solid var(--border-subtle)',
  borderRadius: '8px',
  padding: '8px 12px',
  fontSize: '12px',
};

interface AppointmentStatusChartProps {
  data: AppointmentStatusDistributionResponse | null;
  loading?: boolean;
  error?: boolean;
  incomplete?: boolean;
  title?: string;
}

const AppointmentStatusChart: React.FC<AppointmentStatusChartProps> = ({
  data,
  loading = false,
  error = false,
  incomplete = false,
  title = 'Appointment statistics',
}) => {
  const tc = useTokenColors(STATUS_TOKEN_NAMES);
  const COLORS = {
    confirmed: tc.primary,
    pending: tc['warning-fg'],
    canceled: tc['destructive-fg'],
    completed: tc['success-fg'],
  };

  if (loading) {
    return (
      <div className={styles.chartCard}>
        <div className={styles.chartHeader}>
          <h3 className={styles.chartTitle}>{title}</h3>
        </div>
        <div className={`${styles.chartContainer} ${styles.chartLoading}`}>
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.chartCard}>
        <div className={styles.chartHeader}>
          <h3 className={styles.chartTitle}>{title}</h3>
        </div>
        <div className={`${styles.chartContainer} ${styles.chartError}`}>
          Error generating graph. Please refresh the page.
        </div>
      </div>
    );
  }

  if (incomplete || !data) {
    return (
      <div className={styles.chartCard}>
        <div className={styles.chartHeader}>
          <h3 className={styles.chartTitle}>{title}</h3>
        </div>
        <div className={`${styles.chartContainer} ${styles.chartIncomplete}`}>
          {!data
            ? 'No data available'
            : 'Incomplete data. Graph may not be accurate'}
        </div>
      </div>
    );
  }

  const total = data.confirmed + data.pending + data.canceled + data.completed;
  const chartData = [
    { name: 'Confirmed', value: data.confirmed, color: COLORS.confirmed },
    { name: 'Pending', value: data.pending, color: COLORS.pending },
    { name: 'Canceled', value: data.canceled, color: COLORS.canceled },
    { name: 'Completed', value: data.completed, color: COLORS.completed },
  ].filter((d) => d.value > 0);

  // Custom tooltip component to ensure white text
  const CustomTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload }) => {
    if (!active || !payload || payload.length === 0) {
      return null;
    }

    const item = payload[0];
    const name = item.name || '';
    const value = item.value as number;
    
    // Map status name to color
    const getStatusColor = (statusName: string): string => {
      const statusLower = statusName.toLowerCase();
      if (statusLower === 'confirmed') return COLORS.confirmed;
      if (statusLower === 'pending') return COLORS.pending;
      if (statusLower === 'canceled') return COLORS.canceled;
      if (statusLower === 'completed') return COLORS.completed;
      return item.color || 'var(--text-primary)';
    };
    
    const color = getStatusColor(name);
    const pct = total > 0 ? ((value / total) * 100).toFixed(1) : '0';

    return (
      <div style={tooltipContentStyle}>
        <p style={{ margin: 0, color: 'var(--text-primary)' }}>
          <span style={{ color }}>●</span> {name}: {value} ({pct}%)
        </p>
      </div>
    );
  };

  if (chartData.length === 0) {
    return (
      <div className={styles.chartCard}>
        <div className={styles.chartHeader}>
          <h3 className={styles.chartTitle}>{title}</h3>
        </div>
        <div className={`${styles.chartContainer} ${styles.chartIncomplete}`}>
          No data available
        </div>
      </div>
    );
  }

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartHeader}>
        <h3 className={styles.chartTitle}>{title}</h3>
        <button type="button" className={styles.exportBtn} aria-label="Export">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Export
        </button>
      </div>
      <div className={styles.chartContainer}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius="60%"
              outerRadius="90%"
              paddingAngle={2}
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend
              verticalAlign="bottom"
              height={36}
              iconType="circle"
              iconSize={8}
              formatter={(value, entry: unknown) => {
                const v = Number((entry as { payload?: { value?: number } })?.payload?.value ?? 0);
                const pct = total > 0 ? ((v / total) * 100).toFixed(0) : '0';
                return (
                  <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                    {value} {pct}%
                  </span>
                );
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default AppointmentStatusChart;
